import isEmpty from 'lodash/isEmpty';
import * as QueryString from 'query-string';
import type { FC } from 'react';
import { useEffect } from 'react';

import i18n from '../i18n';
import { isOnboardingPanel } from '../onboarding/onboarding-constants';
import { storeOnboardingData } from '../onboarding/onboarding-data';
import type { DomainInfo } from '../store/api/auth-api';
import { getDomainInfo } from '../store/api/auth-api';
import type { PanelInfo } from '../survey-tool/declarations/ah-api';
import type { LoginResponse, UserProfile } from '../survey-tool/declarations/auth-api';
import { getPureprofileGatewayUrl } from './app-config';
import { getSafeLocale } from './locale';
import { logError } from './log';
import { browserStorageGet, browserStorageSet } from './storage';
import { ensureProtocol, getQueryObj } from './uri';

function isPureprofilePanel() {
  return window.location.origin.endsWith('my.pureprofile.com');
}

function getGatewayUrl() {
  return isPureprofilePanel() ? getPureprofileGatewayUrl() : window.location.origin;
}

function isGatewayUrl(str: string) {
  return str === getGatewayUrl();
}

function isPureprofileGatewayUrl(path: string) {
  return !!(path && path.includes('account.pureprofile.com'));
}

type GatewayRedirectPath = 'accept-terms-and-conditions' | 'validate-email' | 'mobile-number-required';

export function buildGatewayRedirectUrl(
  path: GatewayRedirectPath,
  userProfile: Pick<LoginResponse, 'ppToken' | 'languageCode'>
): string {
  const lang = getSafeLocale(userProfile.languageCode ?? i18n.language);
  return ensureProtocol(`${getGatewayUrl()}/${path}?token=${userProfile.ppToken}&lang=${lang}`);
}

async function getInfo() {
  let domainInfo;
  try {
    domainInfo = browserStorageGet<DomainInfo, null>('domainInfo', null);
    if (!domainInfo) {
      domainInfo = await getDomainInfo(window.location.hostname);
      if (domainInfo && !isEmpty(domainInfo)) {
        browserStorageSet('domainInfo', domainInfo, 'local-storage');
      }
    }
  } catch (err: any) {
    logError(err);
  }
  let panelInfo;
  try {
    panelInfo = browserStorageGet<PanelInfo, null>('panelInfo', null);
  } catch (err: any) {
    logError(err);
  }
  return { domainInfo, panelInfo };
}

export const GoToLoginUrl: FC = () => {
  useEffect(() => {
    goToLoginUrl().catch((loginUrlErr) => logError(loginUrlErr));
  }, []);
  return null;
};

export async function goToLoginUrl(): Promise<void> {
  const { domainInfo, panelInfo } = await getInfo();

  let loginPageUrl = domainInfo?.login_page_url || panelInfo?.login_page_url;
  let webAppLogin = domainInfo?.web_app_login;

  if (!loginPageUrl && webAppLogin) {
    loginPageUrl = `${window.location.origin}/login`;
  }

  if (!loginPageUrl) {
    loginPageUrl = `${getGatewayUrl()}/login`;
  }

  if (loginPageUrl === `${window.location.origin}/login`) {
    webAppLogin = true;
  }

  if ((webAppLogin || isPureprofileGatewayUrl(loginPageUrl)) && !loginPageUrl.includes('?')) {
    const query: Record<string, string | undefined> = {};
    // login & go back to this page (or to redirect page in the uri if present)
    query.redirect = getQueryObj().redirect;
    if (!query.redirect && !['/login', '/logout', '/error'].includes(window.location.pathname)) {
      query.redirect = window.location.href;
    }
    // pass the referrer onto the login page so we know where user is coming from
    query.referrer = getQueryObj().referrer || window.document.referrer || undefined;
    loginPageUrl += `?${QueryString.stringify(query)}`;
  }

  let goTo = ensureProtocol(loginPageUrl);
  if (goTo.endsWith('?')) {
    goTo = goTo.slice(0, -1);
  }
  if (window.location.href !== goTo) {
    // logTrace(`goToLoginUrl ${goTo}`);
    window.location.href = goTo;
  }
}

export function _goToAfterLogoutUrl(_logoutUrl: string) {
  let logoutUrl = _logoutUrl;
  if (isGatewayUrl(logoutUrl)) {
    if (!logoutUrl.includes('/login')) {
      logoutUrl += '/login';
    }
    const { redirect } = getQueryObj();
    if (redirect) {
      logoutUrl += `?redirect=${encodeURIComponent(redirect)}`;
    }
  }
  // logTrace(`_goToAfterLogoutUrl ${logoutUrl}`);
  window.location.href = ensureProtocol(logoutUrl);
}

export async function afterLogoutRedirect() {
  const { domainInfo, panelInfo } = await getInfo();
  const logoutPageUrl = domainInfo?.logout_page_url || panelInfo?.logout_page_url || getGatewayUrl();
  return _goToAfterLogoutUrl(logoutPageUrl);
}

export function goToAcceptTermsAndConditions(
  panelInfo: PanelInfo | null = browserStorageGet<PanelInfo, null>('panelInfo', null),
  userProfile: UserProfile
) {
  if (window.location.pathname !== '/accept-terms-and-conditions') {
    window.location.href = buildGatewayRedirectUrl('accept-terms-and-conditions', userProfile);
  }
}

export function goToValidateEmail(
  panelInfo: PanelInfo | null = browserStorageGet<PanelInfo, null>('panelInfo', null),
  userProfile: UserProfile
) {
  if (isOnboardingPanel(userProfile.panelKey)) {
    if (window.location.pathname !== '/onboarding') {
      storeOnboardingData({ userProfile });
      window.location.href = '/onboarding';
    }
    return;
  }
  if (window.location.pathname !== '/validate-email') {
    window.location.href = buildGatewayRedirectUrl('validate-email', userProfile);
  }
}

export function goToMobileNumberRequired(
  panelInfo: PanelInfo | null = browserStorageGet<PanelInfo, null>('panelInfo', null),
  userProfile: UserProfile
) {
  if (window.location.pathname !== '/mobile-number-required') {
    window.location.href = buildGatewayRedirectUrl('mobile-number-required', userProfile);
  }
}
